/**
 * Root component — router + app chrome.
 *
 * The entry wiring (adapters, QueryClientProvider, ApiProvider, BrowserRouter,
 * OrchestratorProvider, TokensProvider) happens in `main.tsx`. This file only
 * declares the route table + mounts the AuthGuard + the AppShell.
 *
 * T8.3 replaced the T7 placeholders with real feature screens:
 *   - `/` + `/home`              → features/lists/ShoppingListPage
 *   - `/products`                → features/products/ProductsPage
 *   - `/deals`                   → features/deals/DealsPage (merged surface)
 *   - `/settings`                → features/settings/SettingsPage
 *
 * T12.1.5.1.3 merged Offers + Flyer Deals into one `/deals` surface
 * (DealsPage). The two standalone routes `/offers` + `/flyerdeals` now
 * redirect to `/deals`; their page components (OffersPage, FlyerDealsPage)
 * are retired from the router.
 *
 * The AppShell wraps every protected route so header + tab-bar render once.
 */

import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  AuthGuard,
  InviteConsumer,
  OAuthCallback,
  type WebTokenStorage,
} from './auth';
import { LoginPage } from './routes/LoginPage';
import { routes } from './routes/paths';
import { AppShell } from './layout/AppShell';
import { ShoppingListPage } from './features/lists';
import { ProductsPage, HouseholdProductDetailPage } from './features/products';
import { DealsPage } from './features/deals';
import {
  SettingsPage,
  AccountMembersPage,
  MemberDetailPage,
  HouseholdPreferencesPage,
  BrandPreferencesPage,
  StorePreferencesPage,
  BillingPage,
  AppSettingsPage,
} from './features/settings';
import { TripPage } from './features/trip';
import { CardsPage } from './features/cards';
import { FeatureIdeasPage } from './features/feature-ideas';
import { FirstRunSetup, FirstRunGate } from './features/onboarding';
import {
  LegalDocPage,
  LegalIndexPage,
  DeleteAccountPage,
} from './features/legal';
import {
  CreateAccountPage,
  VerifyPage,
  ResetRequestPage,
  ResetPage,
} from './features/auth';

// T28 — the platform-admin console is lazy-loaded so its screens + API client
// land in their own chunk, fetched only when an admin opens `/admin`. Regular
// users never download admin code.
const AdminConsole = lazy(() => import('./features/admin/AdminConsole'));

export interface AppProps {
  tokens: WebTokenStorage;
}

export function App({ tokens }: AppProps): JSX.Element {
  return (
    <Routes>
      {/* Public */}
      <Route path={routes.login} element={<LoginPage tokens={tokens} />} />
      <Route
        path={routes.oauthCallback}
        element={<OAuthCallback tokens={tokens} />}
      />

      {/*
        T27.2.10.1 Phase B — the PUBLIC self-serve auth screens, OUTSIDE
        AuthGuard (sibling to /login). `/register` is Create account; the
        email deep-links land on `/verify/:token` (consume verification) and
        `/reset/:token` (set new password); `/reset` (no token) is the
        forgot-password request form, which the account-exists email also
        links to. All four create / resume a session without a prior one.
      */}
      <Route path={routes.register} element={<CreateAccountPage tokens={tokens} />} />
      <Route path={`${routes.verify}/:token`} element={<VerifyPage tokens={tokens} />} />
      <Route path={routes.resetRequest} element={<ResetRequestPage />} />
      <Route path={`${routes.reset}/:token`} element={<ResetPage tokens={tokens} />} />

      {/*
        T27.2.10.2 — the PUBLIC legal surface. Mounted here, OUTSIDE AuthGuard
        (sibling to /login), so a logged-out Google Play reviewer can deep-link
        the store-listing Privacy Policy URL (/privacy, /terms) and the Data
        Safety "Delete account URL" (/delete-account). Each renders a docs/legal
        draft through LegalDocPage; /legal is the index. No session, no redirect.
      */}
      <Route path={routes.legal} element={<LegalIndexPage />} />
      <Route path={routes.privacy} element={<LegalDocPage slug="privacy" />} />
      <Route path={routes.terms} element={<LegalDocPage slug="terms" />} />
      <Route path={routes.eula} element={<LegalDocPage slug="eula" />} />
      <Route path={routes.cookies} element={<LegalDocPage slug="cookies" />} />
      <Route path={routes.deleteAccount} element={<DeleteAccountPage />} />

      {/*
        T28 — the in-app admin console. Sits inside AuthGuard (a session must
        exist) but OUTSIDE AppShell — the console has its own chrome
        (AdminLayout), not the consumer header/nav/FAB. AdminGuard (inside the
        lazy chunk) redirects a non-admin to /home. The lazy import keeps the
        admin screens out of the main bundle; Suspense covers the chunk fetch.
      */}
      <Route
        path="/admin/*"
        element={
          <AuthGuard tokens={tokens}>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading…</div>}>
              <AdminConsole />
            </Suspense>
          </AuthGuard>
        }
      />

      {/*
        T30.2 — the first-run setup flow. Like /admin it sits INSIDE AuthGuard
        (a session must exist) but OUTSIDE AppShell + FirstRunGate: it is the
        focused full-screen setup destination, not a mode surface, and the gate
        must not redirect the flow onto itself. The FirstRunGate (on the `*`
        branch below) routes a manager whose household has no location here.
      */}
      <Route
        path={routes.onboarding}
        element={
          <AuthGuard tokens={tokens}>
            <FirstRunSetup />
          </AuthGuard>
        }
      />

      {/*
        Protected — everything else.

        T16 Stage 4 Bug 2 fix: <InviteConsumer> sits between AuthGuard and
        the inner Routes so that a signed-in user who lands on `/?invite=X`
        gets the token consumed BEFORE the inner `<Navigate to=/home />`
        strips the query. Mounting it inside AuthGuard guarantees a session
        is present (and so `useTokens()` + the queryClient are populated)
        and inside AppShell so the banner renders within the page chrome.
      */}
      <Route
        path="*"
        element={
          <AuthGuard tokens={tokens}>
            <FirstRunGate>
              <AppShell>
                <InviteConsumer>
                <Routes>
                  <Route
                    path="/"
                    element={<Navigate to={routes.home} replace />}
                  />
                  <Route path={routes.home} element={<ShoppingListPage />} />
                  {/* T12.1.5.1.1 — desktop alias of the planning list, the
                      target T12.1.7.1's "‹ Back to plan" link points at. */}
                  <Route path={routes.plan} element={<ShoppingListPage />} />
                  <Route path={routes.products} element={<ProductsPage />} />
                  <Route
                    path={`${routes.products}/:id`}
                    element={<HouseholdProductDetailPage />}
                  />
                  {/* T12.1.5.1.3 — the merged Deals surface (store offers +
                      flyer deals). /trip and /cards are placeholder screens
                      (T12.1.6 / T12.1.9). */}
                  <Route path={routes.deals} element={<DealsPage />} />
                  <Route path={routes.trip} element={<TripPage />} />
                  <Route path={routes.cards} element={<CardsPage />} />
                  {/* T35.7.2 Part A / F21 — the Feedback tab destination (shell
                      relocated to feature-ideas, no feedback corpus dependency). */}
                  <Route path={routes.feedback} element={<FeatureIdeasPage />} />
                  {/* T12.1.5.1.3 — the two standalone surfaces retired into
                      /deals; old links redirect so nothing strands. */}
                  <Route
                    path={routes.offers}
                    element={<Navigate to={routes.deals} replace />}
                  />
                  <Route
                    path={routes.flyerDeals}
                    element={<Navigate to={routes.deals} replace />}
                  />
                  {/* T12.2 — the grouped settings menu + per-surface routes.
                      `/settings` is the menu landing; each drawer entry routes
                      to its surface. Member detail nests under Account &
                      members. AppShell's `startsWith(/settings)` keeps the
                      layover chrome (Back link, hidden spine/nav/FAB) on all. */}
                  <Route path={routes.settings} element={<SettingsPage />} />
                  <Route
                    path={routes.settingsAccount}
                    element={<AccountMembersPage />}
                  />
                  <Route
                    path={`${routes.settingsMemberDetail}/:userId`}
                    element={<MemberDetailPage />}
                  />
                  <Route
                    path={routes.settingsPreferences}
                    element={<HouseholdPreferencesPage />}
                  />
                  <Route
                    path={routes.settingsBrands}
                    element={<BrandPreferencesPage />}
                  />
                  <Route
                    path={routes.settingsStores}
                    element={<StorePreferencesPage />}
                  />
                  {/* T35.7.2.1 — Billing & subscriptions self-manage surface. */}
                  <Route
                    path={routes.settingsBilling}
                    element={<BillingPage />}
                  />
                  <Route
                    path={routes.settingsApp}
                    element={<AppSettingsPage />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to={routes.home} replace />}
                  />
                </Routes>
                </InviteConsumer>
              </AppShell>
            </FirstRunGate>
          </AuthGuard>
        }
      />
    </Routes>
  );
}
